import { useContext, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { AuthContext } from "../state/AuthContext";

/**
 * GMA-1909 — treat a route CHANGE as user activity, and a MOUNT as nothing.
 *
 * Shared by every authenticated shell (Layout, WebShell) so the two cannot
 * drift. The distinction matters: these shells mount on every route render, so
 * resetting on mount hands out a fresh 30 minutes continuously — including on
 * top of a deadline seeded from an older `auth_time`, which quietly undoes the
 * migration path.
 *
 * Depends on the user ID, never the decoded user OBJECT: `useJwtAsState` builds
 * a new object on cross-tab token events and `baseApi` rewrites the token on
 * every silent refresh, so object identity changes with no user present.
 */
export const useResetSessionOnNavigation = () => {
  const { currentUser, isAuthenticated, resetSessionDeadline } =
    useContext(AuthContext) || {};
  const location = useLocation();
  const previousPathRef = useRef(location.pathname);
  const currentUserId = currentUser?.id ?? null;

  useEffect(() => {
    if (previousPathRef.current === location.pathname) {
      return;
    }
    previousPathRef.current = location.pathname;
    // isAuthenticated as well as the id: a manual logout leaves currentUser
    // populated (useJwtAsState does not re-derive from a same-tab clear) while
    // flipping isAuthenticated false, so the redirect to /login is a route
    // change that would otherwise write a fresh deadline into the storage
    // logout had just emptied - and the next login would inherit it.
    if (
      currentUserId !== null &&
      isAuthenticated !== false &&
      typeof resetSessionDeadline === "function"
    ) {
      resetSessionDeadline();
    }
  }, [location.pathname, currentUserId, isAuthenticated, resetSessionDeadline]);
};

export default useResetSessionOnNavigation;
